import { useEffect, useMemo, useState } from 'react';
import { Send, AlertTriangle, FileText, Receipt, Plus, X, CalendarClock, Gift, Loader2, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { type ItemAutorizacao } from '../../utils/autorizacao-html';
import { BotaoAutorizacao } from './BotaoAutorizacao';
import { BlocoIdentificacaoCliente } from './BlocoIdentificacaoCliente';
import { BlocoOrigemCliente } from './BlocoOrigemCliente';
import { CampoEtiquetasVenda } from './CampoEtiquetasVenda';
import { ListboxEtiquetasVenda } from './ListboxEtiquetasVenda';
import { ModalBuscarCliente } from './ModalBuscarCliente';
import { atendimentoApi } from '../../modules/atendimento/lib/api';
import { useEtiquetas } from '../../modules/atendimento/lib/etiquetas-context';
import {
  alternar as alternarEtiqueta, ordenarParaOBalcao, ESTADO_VAZIO, type EstadoEtiquetas,
} from './etiquetas-do-balcao';
import { SimulacaoParcelamentoWhatsApp, type ResultadoParcelamento, type EstadoParcelamento } from '../pagamento/SimulacaoParcelamentoWhatsApp';
import { carteiraPadrao, saldoAbativel, pedidoIdentificado, numeroPodeSerWhatsapp, type CarteiraCredito } from '@fiodecobre/shared';

const fmt = (v: number | string): string =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtMoney(val: number | string | null | undefined): string {
  if (val === '' || val === null || val === undefined) return '';
  let n: number;
  if (typeof val === 'number') {
    n = val;
  } else {
    const s = String(val);
    if (/^-?\d+(\.\d+)?$/.test(s)) {
      n = Number(s);
    } else {
      n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
    }
  }
  if (isNaN(n)) return String(val);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseMoney(val: string | number | null | undefined): number {
  if (val === '' || val === null || val === undefined) return 0;
  if (typeof val === 'number') return val;
  return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0;
}

export interface FormaPagamentoModal {
  id: number;
  nome: string;
  tipo: string;
  permite_desconto: boolean;
}

interface PagamentoLinha {
  forma_pagamento_id: number;
  valor: number | string;
  dias_vencimento?: number;
  recebido?: number;
  card_parcelas?: number;
  conta_cartao?: string; // 'mp' | 'pagbank' — maquininha escolhida, carrega pro caixa
  credito_origem?: CarteiraCredito; // 'troca' | 'programa' — qual carteira o credito consome (DA-436)
}

// Cotacao de parcelamento no cartao (so EXIBE): tipos ParcelaCalculada/ResultadoParcelamento/
// EstadoParcelamento vem da peca UNICA de simulacao (../pagamento/SimulacaoParcelamentoWhatsApp).
// Endpoint por conta: /api/cartao/parcelamento (MP/Point) ou /api/caixa/parcelamento (PagBank).
// Contas/maquininhas que cotam parcelamento ao vivo. InfinitePay fica de fora (sem API de taxas).
const CONTAS_CARTAO_BASE = [
  { id: 'mp', nome: 'Mercado Pago', endpoint: '/api/cartao/parcelamento' },
  { id: 'pagbank', nome: 'PagBank', endpoint: '/api/caixa/parcelamento' },
] as const;

export interface ModalEnviarCaixaProps {
  orcamentoId: number | null;
  clienteId: number;
  clienteNome: string;
  clienteDoc?: string | null;
  numeroOrcamento?: string | null;
  comprador?: string;
  itensOrcamento?: ItemAutorizacao[];
  formasPagamento: FormaPagamentoModal[];
  formaPagamentoId: number | null;
  totalOrcamento: number | string;
  totalSemDesconto: number | string;
  temDescontos: boolean;
  /** Envio em andamento (transacao pesada no backend): trava o botao + mostra "Enviando...". */
  enviando?: boolean;
  onConfirm: (tipoDocumento: string, pagamentos: PagamentoLinha[], cpfNota: string | null) => void;
  onCancel: () => void;

  // ── Trava de identificacao do cliente ([DA-697], 09/09/2026) ──────────────────────────────
  /** "Nao quis informar o Telefone" ja marcado no pedido (migration 0277). */
  telefoneRecusado?: boolean;
  /** Contato que esta venda capturou (migration 0281). */
  contatoCapturadoId?: number | null;
  /** Cliente escolhido no seletor que abre DE DENTRO deste modal — o pai grava e o autosave leva. */
  onEscolherCliente?: (c: ClienteEscolhido) => void;
  /** O X na linha do cliente: o pedido volta pra Consumidor (o pai limpa, o autosave leva). */
  onRemoverCliente?: () => void;
  /** Telefone capturado aqui: o pai guarda o id pra trava soltar sem esperar recarga. */
  onCapturouContato?: (contatoId: number, nome: string) => void;
  /** Nome e etiquetas da recusa ja gravados no pedido ([DA-780], migration 0307). */
  recusaNome?: string | null;
  recusaEtiquetaIds?: number[];
  /**
   * A recusa, ja GRAVADA pela rota `registrar-recusa` (marca + nome + etiquetas num gesto so'). O
   * pai so' espelha o estado — ⛔ desde a [DA-780] ela nao e' mais um clique: nome e etiqueta sao
   * obrigatorios, decisao dele.
   */
  onRecusarTelefone?: (nome: string, etiquetaIds: number[]) => void;
}

/** O que o `ModalBuscarCliente` devolve e o orcamento precisa pra trocar de cliente. */
export interface ClienteEscolhido {
  id: number;
  nome: string;
  codigo_legado?: string | null;
  consignacao?: boolean;
}

export function ModalEnviarCaixa({
  orcamentoId,
  clienteId,
  clienteNome,
  clienteDoc,
  numeroOrcamento,
  comprador,
  itensOrcamento,
  formasPagamento,
  formaPagamentoId,
  totalOrcamento: totalOrcamentoRaw,
  totalSemDesconto: totalSemDescontoRaw,
  temDescontos,
  enviando = false,
  onConfirm,
  onCancel,
  telefoneRecusado = false,
  contatoCapturadoId = null,
  recusaNome = null,
  recusaEtiquetaIds = [],
  onEscolherCliente,
  onRemoverCliente,
  onCapturouContato,
  onRecusarTelefone,
}: ModalEnviarCaixaProps) {
  const { usuario } = useAuth();
  const totalOrcamento = Number(totalOrcamentoRaw) || 0;
  const totalSemDesconto = Number(totalSemDescontoRaw) || 0;
  const [tipoDocumento, setTipoDocumento] = useState('nfce');
  const [cpfNota, setCpfNota] = useState('');

  // Cliente travado em A4 no cadastro (migration 0263). O modal so recebe id e nome, entao pergunta
  // ao cadastro na abertura em vez de exigir que cada tela dona propague a flag — e' 1 chamada, so
  // quando o modal abre. Enquanto nao responde, o toggle segue como sempre; quando responde travado,
  // o botao NFC-e some e a marcacao vira 'nfe'. O backend recusa igual ([enviar-caixa]) — a tela so
  // evita oferecer o que o servidor rejeita ([DA-617]).
  const [travadoA4, setTravadoA4] = useState(false);
  // A MESMA chamada responde a trava de identificacao ([DA-697]): a saida "cliente do cadastro"
  // so' vale se a ficha tiver telefone. 17 vendas em 30 dias foram pra fichas sem numero nenhum
  // (e 83 dos 297 clientes estao assim), entao escolher o cliente sozinho satisfaria a trava sem
  // capturar contato algum.
  // ⚠️ Comeca `null` = "ainda nao sei", nunca `true`/`false`: um default errado aqui pisca a
  // faixa de cobranca em cliente que tem numero, ou libera o envio de quem nao tem ([DA-649]).
  const [clienteTemNumero, setClienteTemNumero] = useState<boolean | null>(null);
  useEffect(() => {
    if (!clienteId || clienteId === 1) { setTravadoA4(false); setClienteTemNumero(null); return; }
    let vivo = true;
    api.get<{ travar_nota_a4?: boolean; whatsapp?: string | null; telefone?: string | null }>(`/api/clientes/${clienteId}`)
      .then((c) => {
        if (!vivo) return;
        if (c?.travar_nota_a4) { setTravadoA4(true); setTipoDocumento('nfe'); }
        // ⛔ FORMATO, nao presenca: fixo na ficha nao recebe fly ([DA-698] — a `3RN CAPITAL`
        // passava direto com um 6233753529). A regra e' a MESMA peca do servidor.
        setClienteTemNumero(
          numeroPodeSerWhatsapp(c?.whatsapp) || numeroPodeSerWhatsapp(c?.telefone),
        );
      })
      .catch(() => { /* cadastro fora do ar nao pode travar o envio: o backend ainda recusa */ });
    return () => { vivo = false; };
  }, [clienteId]);
  // ── Trava de identificacao ([DA-697]) ──────────────────────────────────────────────────────
  // O `capturadoAgora` existe pra a trava soltar no MESMO gesto: a rota de captura carimba o
  // pedido no banco, mas a prop `contatoCapturadoId` so' chegaria na proxima leitura do pai.
  const [capturadoAgora, setCapturadoAgora] = useState<number | null>(null);
  // ⛔ Quem grava faz o bloco inteiro SUMIR (a trava soltou) — e ate 09/09/2026 nada dizia o que
  // ficou gravado. Com a ficha de cliente entrando por aqui ([DA-698]) o gesto passou a mudar o
  // DONO da venda: tela que troca o dono e nao diz nada se le como "clicou e sumiu".
  const [capturadoNome, setCapturadoNome] = useState<string | null>(null);
  // O recibo da recusa ([DA-780]): mesmo motivo do `capturadoNome` — o bloco some quando grava, e
  // sem uma linha dizendo o que ficou registrado o gesto le como "clicou e sumiu".
  const [recusadoAgora, setRecusadoAgora] = useState<string | null>(null);
  const [showBuscaCliente, setShowBuscaCliente] = useState(false);
  // A origem do cliente foi marcada NESTE modal: a janela em que a vendedora ainda troca (decisao
  // dele, 16/10/2026 — respondida uma vez, trava). Mora aqui porque o bloco remonta ao mudar de
  // coluna quando o telefone e' gravado.
  const [origemMarcadaAqui, setOrigemMarcadaAqui] = useState(false);

  // ── AS ETIQUETAS DO BALCAO ([DA-723]) ───────────────────────────────────────────────────────
  // ⛔ **O estado mora AQUI, nao no bloco de identificacao** — desde 12/09 a lista e' a coluna do
  // MEIO do modal (pedido dele), e coluna de grid tem que ser IRMA das outras duas. Quem GRAVA
  // segue sendo o botao do caminho 2, no bloco: campo que so' vale se um segundo botao for
  // apertado e' o defeito que a [DA-707] pagou.
  const { paraCampanha, todas: etiquetasTodas, atualizar: atualizarEtiquetas } = useEtiquetas();
  // ⚠️ **A ordem se garante AQUI, nao na rota**, e ela e' a peca compartilhada com o caminho 1 —
  // `CONSUMIDOR` primeiro, o resto alfabetico.
  // ⛔ `paraCampanha`, nunca `visiveis`: a `nao_cliente` esconde o contato da Gestao de Vendas
  // inteira ([DA-693]) e ja custou R$ 57.734,26 sumindo da tela ([DA-717]).
  const etiquetasDoCombo = useMemo(() => ordenarParaOBalcao(paraCampanha), [paraCampanha]);
  const [etq, setEtq] = useState<EstadoEtiquetas>(ESTADO_VAZIO);
  // A exigencia nasce no bloco (numero completo + com WhatsApp) e sobe pra ca so' pra a coluna do
  // meio acender o `*`: a mensagem que explica o botao cinza continua colada no botao.
  const [etqPendente, setEtqPendente] = useState(false);
  // ⛔ O provider e' global (`main.tsx`) mas quem CARREGA a lista sao o Atendimento e a Gestao de
  // Vendas — o caixa nao passa por nenhum dos dois. Sem esta linha a coluna abriria vazia com 28
  // etiquetas no banco: ausencia de carga lida como inexistencia ([DA-468]).
  useEffect(() => {
    if (etiquetasTodas.length) return;
    atendimentoApi.listarEtiquetas().then(atualizarEtiquetas).catch(() => {});
  }, [etiquetasTodas.length, atualizarEtiquetas]);

  // A regra e' a MESMA peca do servidor (`pedidoIdentificado`, no shared, com teste): a tela nao
  // pode travar o que o backend aceitaria, nem liberar o que ele vai negar.
  // ⚠️ `clienteTemNumero` entra como esta — `null` significa "ainda nao perguntei ao cadastro", e
  // a funcao mantem a trava de pe nesse estado, de proposito.
  const faltaIdentificar = !pedidoIdentificado({
    clienteId,
    clienteTemNumero,
    contatoCapturadoId: contatoCapturadoId ?? capturadoAgora,
    telefoneRecusado,
    recusaNome,
    recusaEtiquetas: recusaEtiquetaIds.length,
  });
  // [DA-867]: cliente do cadastro sem WhatsApp ja nao trava (`faltaIdentificar` e' falso), mas o
  // campo de informar o numero continua aparecendo — OPCIONAL (decisao dele, 30/10/2026). Por isso
  // a tela se desenha por `mostrarIdentificacao` e o botao Enviar segue so' o `faltaIdentificar`.
  const whatsappOpcional = clienteId > 1 && clienteTemNumero === false
    && (contatoCapturadoId ?? capturadoAgora) == null;
  const mostrarIdentificacao = faltaIdentificar || whatsappOpcional;

  const descontoTotal = (totalSemDesconto || totalOrcamento) - totalOrcamento;
  const descontoPercentual = totalSemDesconto > 0 ? (descontoTotal / totalSemDesconto) * 100 : 0;

  // Orcamento com desconto = incentivo pra pix/dinheiro (cartao paga cheio, DA-076): a prevenda
  // nasce no PIX pra nao abrir no cartao e "apagar" o desconto na cara do vendedor. Sem desconto,
  // mantem o default de sempre (forma do orcamento, senao a 1a da lista). Ele troca se quiser.
  const fpPix = formasPagamento.find((f) => f.tipo === 'pix')?.id;
  const fpInicial = (descontoTotal > 0.005 && fpPix) ? fpPix : (formaPagamentoId || formasPagamento[0]?.id);
  const [pagamentos, setPagamentos] = useState<PagamentoLinha[]>([
    { forma_pagamento_id: fpInicial!, valor: totalOrcamento || 0, dias_vencimento: 15 },
  ]);
  const [editingValores, setEditingValores] = useState<Record<number, string>>({});
  const [editingRecebidos, setEditingRecebidos] = useState<Record<number, string>>({});
  // Cotacao de parcelamento por linha (chave = indice) e parcela escolhida (so exibe).
  const [parcelamentos, setParcelamentos] = useState<Record<number, EstadoParcelamento>>({});
  const [parcelasSel, setParcelasSel] = useState<Record<number, number>>({});
  // Conta/maquininha escolhida por linha. Vazio = ainda nao escolheu → parcelas travadas.
  const [contaSel, setContaSel] = useState<Record<number, string>>({});
  // Conta pre-selecionada por padrao = o "Caixa destino (Default)" de Configuracoes → Recebimentos,
  // mapeado pra mp/pagbank (se o default for um caixa de cartao). '' = sem default aplicavel.
  const [contaDefault, setContaDefault] = useState('');
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const [cfg, contas] = await Promise.all([
        api.get<{ valor: string }>('/api/app-settings/caixa_destino_default').catch(() => ({ valor: '' })),
        api.get<Array<{ id: number; nome: string }>>('/api/caixa/contas').catch(() => [] as Array<{ id: number; nome: string }>),
      ]);
      if (cancelado) return;
      const nome = contas.find((c) => c.id === Number(cfg?.valor))?.nome ?? '';
      const id = /mercado\s*pago/i.test(nome) ? 'mp' : /pag\s*bank|pagbank/i.test(nome) ? 'pagbank' : '';
      setContaDefault(id);
    })();
    return () => { cancelado = true; };
  }, []);
  // Maquininha Point (MP) configurada (token MP + POINT_DEVICE_ID): so entao o Mercado Pago entra na
  // lista de contas que cotam (senao o /api/cartao/parcelamento daria erro).
  const [cartaoMaquininha, setCartaoMaquininha] = useState(false);
  useEffect(() => {
    let cancelado = false;
    api.get<{ configurada: boolean }>('/api/cartao/status')
      .then((s) => { if (!cancelado) setCartaoMaquininha(!!s.configurada); })
      .catch(() => { /* sem Point → so PagBank na lista */ });
    return () => { cancelado = true; };
  }, []);
  // Contas/maquininhas oferecidas pra cotar (MP so se a Point estiver ligada).
  const contasCartao = CONTAS_CARTAO_BASE.filter((c) => c.id !== 'mp' || cartaoMaquininha);
  // Conta efetiva de uma linha: a escolhida, senao o default — desde que esteja entre as disponiveis
  // (senao '' = ainda nao escolheu). '' explicito (usuario voltou pra "Selecione") NAO cai no default.
  const contaEfetiva = (i: number): string => {
    const c = contaSel[i] ?? contaDefault;
    return contasCartao.some((x) => x.id === c) ? c : '';
  };

  // Razao cheio/liquido do orcamento — converte o valor da linha de cartao no
  // preco cheio dela. Fallback 1 quando nao da pra inferir o desconto.
  const ratioCheio = totalOrcamento > 0 && totalSemDesconto >= totalOrcamento
    ? totalSemDesconto / totalOrcamento
    : 1;

  const isConsumidor = clienteId === 1;
  const nfeComConsumidor = tipoDocumento === 'nfe' && isConsumidor;

  // Credito da Loja — busca saldo ao selecionar
  const [saldoCredito, setSaldoCredito] = useState<number | null>(null);
  // Qual carteira o `saldoCredito` acima representa (DA-436). Viaja no payload pro caixa nao
  // reescolher sozinho e capar pelo saldo do outro lado.
  const [carteiraCredito, setCarteiraCredito] = useState<CarteiraCredito>('troca');
  const [creditoLoading, setCreditoLoading] = useState(false);

  function getFp(fpId: number): FormaPagamentoModal | undefined {
    return formasPagamento.find((f) => f.id === fpId);
  }
  function fpPermiteDesconto(fpId: number): boolean {
    const fp = getFp(fpId);
    return fp ? fp.permite_desconto !== false : true;
  }
  function fpIsCrediario(fpId: number): boolean {
    return getFp(fpId)?.tipo === 'crediario';
  }
  function fpIsCredito(fpId: number): boolean {
    return getFp(fpId)?.tipo === 'credito_loja';
  }
  function fpIsDinheiro(fpId: number): boolean {
    return getFp(fpId)?.tipo === 'dinheiro';
  }

  const temCreditoSelecionado = pagamentos.some((p) => fpIsCredito(p.forma_pagamento_id));

  useEffect(() => {
    if (!temCreditoSelecionado || isConsumidor || !clienteId) {
      setSaldoCredito(null);
      return;
    }
    setCreditoLoading(true);
    // Saldo de credito da loja ativo do cliente (mesmo endpoint usado pelo Caixa).
    //
    // ⚠️ O que se anuncia aqui e' o MAIOR saldo entre as duas carteiras, NUNCA a soma (DA-436):
    // uma compra aceita um unico credito, entao a soma prometeria um abatimento impossivel. Com
    // R$ 100 de troca + R$ 400 do programa, a tela chegou a dizer "Saldo disponivel R$ 500,00" —
    // a vendedora prometia R$ 500 ao cliente e o caixa so entregava R$ 400.
    api
      .get<{ saldo_troca: number; saldo_programa: number }>(`/api/creditos/cliente/${clienteId}/saldo`)
      .then((res) => {
        const troca = Number(res?.saldo_troca) || 0;
        const programa = Number(res?.saldo_programa) || 0;
        setSaldoCredito(saldoAbativel(troca, programa));
        setCarteiraCredito(carteiraPadrao(troca, programa));
      })
      .catch(() => setSaldoCredito(0))
      .finally(() => setCreditoLoading(false));
  }, [temCreditoSelecionado, clienteId, isConsumidor]);

  const creditoComConsumidor = temCreditoSelecionado && isConsumidor;
  const creditoSemSaldo = temCreditoSelecionado && saldoCredito === 0 && !creditoLoading;

  // Auto-preencher valor do credito quando saldo chega
  useEffect(() => {
    if (saldoCredito === null || saldoCredito <= 0) return;
    const idx = pagamentos.findIndex((p) => fpIsCredito(p.forma_pagamento_id));
    if (idx < 0) return;
    const outrosTotal = pagamentos.reduce(
      (acc, p, i) => (i === idx ? acc : acc + (parseFloat(String(p.valor)) || 0)),
      0,
    );
    const restanteParaCredito = Math.max(0, Math.round((totalOrcamento - outrosTotal) * 100) / 100);
    const valorCredito = Math.min(saldoCredito, restanteParaCredito);
    let novos = pagamentos;
    if (Math.abs((parseFloat(String(pagamentos[idx]!.valor)) || 0) - valorCredito) > 0.01) {
      novos = pagamentos.map((p, i) => (i === idx ? { ...p, valor: valorCredito } : p));
      if (novos.length === 2) {
        const outro = idx === 0 ? 1 : 0;
        novos[outro]!.valor = Math.max(0, Math.round((totalOrcamento - valorCredito) * 100) / 100);
      }
    }
    // Credito capado no saldo deixa uma lacuna (saldo R$ 51 num total R$ 52 sobra R$ 1) e o pedido
    // tem SO a linha de credito: cria a linha de Dinheiro com o restante ja preenchido, em vez de so
    // avisar "Falta R$ X" e obrigar o operador a achar o botao "Adicionar". Espelha o caixa, que
    // derruba o restante numa linha de dinheiro sozinho.
    const gap = Math.round((totalOrcamento - outrosTotal - valorCredito) * 100) / 100;
    if (gap > 0.01 && novos.length === 1) {
      const dinheiro = formasPagamento.find(
        (f) => f.tipo === 'dinheiro' && f.id !== pagamentos[idx]!.forma_pagamento_id,
      ) ?? formasPagamento.find((f) => !novos.some((p) => p.forma_pagamento_id === f.id));
      if (dinheiro) novos = [...novos, { forma_pagamento_id: dinheiro.id, valor: gap, dias_vencimento: 15 }];
    }
    if (novos !== pagamentos) setPagamentos(novos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saldoCredito]);

  // Assinatura (indice:centavos:conta) das linhas de cartao — dispara a cotacao viva so quando muda a
  // linha, o valor OU a conta escolhida. Sem conta escolhida a linha nao entra (parcelas travadas).
  const cartaoSig = pagamentos
    .map((p, i) => (getFp(p.forma_pagamento_id)?.tipo === 'cartao_credito'
      ? `${i}:${Math.round((parseFloat(String(p.valor)) || 0) * 100)}:${contaEfetiva(i)}`
      : ''))
    .filter(Boolean)
    .join('|');

  useEffect(() => {
    const alvos = pagamentos
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => getFp(p.forma_pagamento_id)?.tipo === 'cartao_credito');
    if (alvos.length === 0) return;
    let cancelado = false;
    const timer = setTimeout(() => {
      alvos.forEach(({ p, i }) => {
        const conta = CONTAS_CARTAO_BASE.find((c) => c.id === contaEfetiva(i));
        if (!conta) return; // sem conta escolhida → nao cota (parcelas travadas)
        const liq = parseFloat(String(p.valor)) || 0;
        const liquidoC = Math.round(liq * 100);
        if (liquidoC <= 0) return;
        const cheioC = Math.round(liq * ratioCheio * 100);
        setParcelamentos((m) => ({ ...m, [i]: { ...m[i], loading: true, erro: undefined } }));
        // Cada conta cota no seu endpoint: Mercado Pago (Point) e PagBank tem taxas proprias.
        api
          .get<ResultadoParcelamento>(`${conta.endpoint}?liquido=${liquidoC}&cheio=${cheioC}`)
          .then((r) => { if (!cancelado) setParcelamentos((m) => ({ ...m, [i]: { loading: false, data: r } })); })
          .catch((e: any) => { if (!cancelado) setParcelamentos((m) => ({ ...m, [i]: { loading: false, erro: e.message } })); });
      });
    }, 400);
    return () => { cancelado = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartaoSig, ratioCheio]);

  function calcTotalEfetivo(pags: PagamentoLinha[]): number {
    if (descontoTotal <= 0) return totalOrcamento;
    let baseComDesc = 0;
    let baseSemDesc = 0;
    for (const p of pags) {
      const val = parseFloat(String(p.valor)) || 0;
      if (fpPermiteDesconto(p.forma_pagamento_id)) baseComDesc += val;
      else baseSemDesc += val;
    }
    if (baseSemDesc <= 0) return totalOrcamento;
    const fator = totalOrcamento > 0 ? totalSemDesconto / totalOrcamento : 1;
    const semDescAjustado = Math.round(baseSemDesc * fator * 100) / 100;
    return Math.round((baseComDesc + semDescAjustado) * 100) / 100;
  }

  const totalEfetivo = calcTotalEfetivo(pagamentos);
  const totalPago = pagamentos.reduce((acc, p) => acc + (parseFloat(String(p.valor)) || 0), 0);
  const restante = Math.round((totalOrcamento - totalPago) * 100) / 100;
  // Cobertura: as formas de pagamento tem que somar o total do orcamento. Fica > 0 quando falta
  // forma pra uma parte (ex.: Credito da Loja capado no saldo — saldo R$ 34 num total R$ 35,89
  // deixa R$ 1,89 sem forma). Cartao infla a linha (soma passa do total) → restante <=0 = coberto.
  const faltaCobertura = restante > 0.01;

  const temSemDescSelecionado = pagamentos.some(
    (p) => !fpPermiteDesconto(p.forma_pagamento_id) && (parseFloat(String(p.valor)) || 0) > 0,
  );
  const descontoRemovido = temSemDescSelecionado
    ? Math.round((totalEfetivo - totalOrcamento) * 100) / 100
    : 0;

  const temCrediario = pagamentos.some(
    (p) => fpIsCrediario(p.forma_pagamento_id) && (parseFloat(String(p.valor)) || 0) > 0,
  );
  const crediarioComConsumidor = temCrediario && isConsumidor;
  const crediarioDescontoAcima5 = temCrediario && Math.round(descontoPercentual * 10) / 10 > 5;

  function formasDisponiveis(currentFpId: number): FormaPagamentoModal[] {
    const usados = pagamentos.map((p) => p.forma_pagamento_id).filter((id) => id !== currentFpId);
    return formasPagamento.filter((f) => !usados.includes(f.id));
  }

  function adicionarPagamento() {
    const disponiveis = formasPagamento.filter(
      (f) => !pagamentos.some((p) => p.forma_pagamento_id === f.id),
    );
    if (disponiveis.length === 0) return;
    // Recebimento ja coberto: linha nova nasceria 0,00, que o envio descarta em silencio e que
    // ainda dispara as travas de credito da loja sem consumir nada. O botao ja vem desabilitado
    // neste estado; isto e a rede de baixo (DA-582).
    if (restante <= 0.01) return;
    const valorRestante = Math.max(0, Math.round(restante * 100) / 100);
    const novaFp = disponiveis[0]!;
    let valorNovo = valorRestante;
    if (!novaFp.permite_desconto && descontoTotal > 0 && valorRestante > 0) {
      const fator = totalOrcamento > 0 ? totalSemDesconto / totalOrcamento : 1;
      valorNovo = Math.round(valorRestante * fator * 100) / 100;
    }
    setPagamentos([
      ...pagamentos,
      { forma_pagamento_id: novaFp.id, valor: valorNovo, dias_vencimento: 15 },
    ]);
  }

  function removerPagamento(idx: number) {
    if (pagamentos.length <= 1) return;
    const novos = pagamentos.filter((_, i) => i !== idx);
    if (novos.length === 1) {
      novos[0]!.valor = fpPermiteDesconto(novos[0]!.forma_pagamento_id) ? totalOrcamento : totalSemDesconto;
    }
    setPagamentos(novos);
  }

  function atualizarPagamento(idx: number, campo: 'valor' | 'forma_pagamento_id' | 'dias_vencimento', valor: string | number) {
    const novos: PagamentoLinha[] = pagamentos.map((p, i) => {
      if (i !== idx) return p;
      if (campo === 'valor') return { ...p, valor: valor === '' ? '' : parseFloat(String(valor)) || 0 };
      if (campo === 'dias_vencimento') return { ...p, dias_vencimento: parseInt(String(valor)) || 0 };
      if (campo === 'forma_pagamento_id') return { ...p, forma_pagamento_id: Number(valor) };
      return p;
    });

    if (campo === 'forma_pagamento_id' && novos.length === 2) {
      const outro = idx === 0 ? 1 : 0;
      const outroVal = parseFloat(String(novos[outro]!.valor)) || 0;
      const sobra = totalOrcamento - outroVal;
      const novaFp = formasPagamento.find((f) => f.id === Number(valor));

      if (novaFp && !novaFp.permite_desconto && descontoTotal > 0 && sobra > 0) {
        const fator = totalOrcamento > 0 ? totalSemDesconto / totalOrcamento : 1;
        novos[idx]!.valor = Math.round(sobra * fator * 100) / 100;
      } else if (!fpPermiteDesconto(novos[outro]!.forma_pagamento_id) && descontoTotal > 0) {
        const meuVal = parseFloat(String(novos[idx]!.valor)) || 0;
        const sobraOutro = totalOrcamento - meuVal;
        const fator = totalOrcamento > 0 ? totalSemDesconto / totalOrcamento : 1;
        novos[outro]!.valor = Math.round(sobraOutro * fator * 100) / 100;
      } else {
        novos[idx]!.valor = Math.max(0, Math.round(sobra * 100) / 100);
      }
    }

    setPagamentos(novos);
  }

  function handleValorBlur(idx: number) {
    const totalPagoAtual = pagamentos.reduce((acc, p) => acc + (parseFloat(String(p.valor)) || 0), 0);
    const sobra = Math.round((totalOrcamento - totalPagoAtual) * 100) / 100;

    // ⛔ Ate 19/08/2026, baixar a ULTIMA linha criava sozinha uma linha nova com a sobra, na
    // primeira forma que estivesse livre — e ainda roubava o foco pra ela. Removido (DA-582): a
    // forma era escolhida pelo sistema (saia "Dinheiro" quando o cliente ia pagar em cartao) e o
    // estado "falta valor" morria no blur, deixando o "Adicionar forma de pagamento" apagado pra
    // sempre. Agora a sobra fica VISIVEL (banner laranja + Enviar travado) e quem escolhe a segunda
    // forma e o operador, pelo botao — a mesma coreografia da tela de finalizar venda.

    if (idx < pagamentos.length - 1 && pagamentos.length >= 2) {
      const ultimo = pagamentos.length - 1;
      const totalSemUltimo = pagamentos.reduce(
        (acc, p, i) => (i === ultimo ? acc : acc + (parseFloat(String(p.valor)) || 0)),
        0,
      );
      const sobraParaUltimo = Math.max(0, Math.round((totalOrcamento - totalSemUltimo) * 100) / 100);

      let valorUltimo: number;
      if (
        !fpPermiteDesconto(pagamentos[ultimo]!.forma_pagamento_id) &&
        descontoTotal > 0 &&
        sobraParaUltimo > 0
      ) {
        const fator = totalOrcamento > 0 ? totalSemDesconto / totalOrcamento : 1;
        valorUltimo = Math.round(sobraParaUltimo * fator * 100) / 100;
      } else {
        valorUltimo = sobraParaUltimo;
      }

      setPagamentos(pagamentos.map((p, i) => (i === ultimo ? { ...p, valor: valorUltimo } : p)));
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLElement).blur();
      setTimeout(() => {
        const focusable = Array.from(
          document.querySelectorAll<HTMLElement>('select, input, button:not([disabled])'),
        ).filter((el) => el.offsetParent !== null && el.tabIndex !== -1);
        const currentIdx = focusable.indexOf(e.target as HTMLElement);
        if (document.activeElement === document.body || document.activeElement === e.target) {
          const next = focusable[currentIdx + 1];
          if (next) next.focus();
        }
      }, 80);
    }
  }

  function handleConfirm() {
    if (enviando) return; // envio ja em curso — ignora clique/Enter repetido
    if (nfeComConsumidor) return;
    if (crediarioComConsumidor) {
      alert('Crediario exige cliente cadastrado.');
      return;
    }
    if (crediarioDescontoAcima5) {
      alert('Crediario permite no maximo 5% de desconto.');
      return;
    }
    if (creditoComConsumidor) {
      alert('Credito da Loja exige cliente cadastrado.');
      return;
    }
    if (creditoSemSaldo) {
      alert('Cliente sem credito disponivel.');
      return;
    }

    const pagCredito = pagamentos.find((p) => fpIsCredito(p.forma_pagamento_id));
    if (pagCredito && saldoCredito !== null && (parseFloat(String(pagCredito.valor)) || 0) > saldoCredito) {
      alert(
        `Valor do credito (R$ ${fmt(pagCredito.valor)}) excede o saldo disponivel (R$ ${fmt(saldoCredito)}).`,
      );
      return;
    }

    // Carrega a parcela E a maquininha escolhida nas linhas de cartao (indexada pela ordem
    // ANTES do filtro, igual o parcelasSel/contaSel). So o numero e a conta; o valor "cobrar
    // na maquininha" recalcula ao vivo no caixa na adquirente certa.
    const pagamentosValidos = pagamentos
      .map((p, idx) =>
        getFp(p.forma_pagamento_id)?.tipo === 'cartao_credito'
          ? { ...p, card_parcelas: parcelasSel[idx] ?? 1, conta_cartao: contaEfetiva(idx) || undefined }
          : p,
      )
      // A carteira que o saldo anunciado acima representa viaja junto (DA-436). Sem isto o caixa
      // reescolheria pelo saldo de la e podia cair no outro lado — a vendedora prometeria o
      // abatimento de uma carteira e o caixa capava pelo da outra.
      .map((p) =>
        fpIsCredito(p.forma_pagamento_id) ? { ...p, credito_origem: carteiraCredito } : p,
      )
      .filter((p) => (parseFloat(String(p.valor)) || 0) > 0);
    if (pagamentosValidos.length === 0) {
      alert('Informe pelo menos uma forma de pagamento.');
      return;
    }

    onConfirm(tipoDocumento, pagamentosValidos, cpfNota.replace(/\D/g, '') || null);
  }

  return (
    // `px-4`: o modal agora pode chegar a 1400px, e sem respiro ele encostaria nas bordas em
    // janela mais estreita — "o maximo horizontal" e' ate a margem, nao ate o corte.
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      {/* ⚠️ **A largura depende de haver DUAS colunas pra encher** (pedido dele em 12/09/2026:
          *"aumente o maximo horizontal da tela sem precisar rolar"* + *"passe o bloco para uma
          outra coluna ao lado"*).
          ⛔ **Sem o bloco de identificacao o modal VOLTA a 764px**, e nao e' detalhe: ele so'
          aparece quando falta identificar — nas 341 vendas/mes que ja tem cliente com telefone
          um modal de 1400px teria metade vazia, e a coluna sobrando leria como tela quebrada.
          ⚠️ `max-w-2xl` era **588px**, nao 672: a raiz do app e' 14px, entao 42rem da' 588 —
          converter na hora e' a regra, porque o numero da classe nao e' o numero de pixels. */}
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-h-[94vh] flex flex-col ${mostrarIdentificacao ? 'max-w-[1400px]' : 'max-w-[764px]'}`}
        style={{ position: 'relative' }}
      >
        <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={onCancel} aria-label="Fechar">×</button>
        <h3 className="text-lg font-semibold text-gray-800 px-6 pt-6 pb-3 flex-shrink-0">Enviar ao Caixa</h3>

        {/* Miolo rolavel so se faltar altura; cabecalho e botoes ficam fixos (nunca somem) */}
        <div className="px-6 flex-1 overflow-y-auto min-h-0">
        {/* ── TRES COLUNAS quando ha o que identificar ([DA-723], pedido dele) ─────────────────
            Esquerda: quem esta comprando. Meio: as etiquetas. Direita: nota, pagamento e totais.
            ⚠️ **4/2/4 e' o pedido dele medido** (*"diminua cada uma das duas colunas em 20% e
            utilize esta sobra para criar a coluna do meio"*): as laterais caem de 50% pra 40% do
            espaco de trilha e o meio fica com os 20% liberados. Medido em prod na janela dele
            (1536x740): 668,5 → **526,4 / 263,2 / 526,4**.
            ⛔ **`minmax(0,Nfr)` em TODAS**, nao so' nas que encolhem: `1fr` puro e' `minmax(auto,1fr)`
            e o `auto` e' um PISO — a coluna com o item mais largo trava no tamanho dele e come a
            largura das outras ([DA-602]).
            ⚠️ **`lg:` (1024px) segue sendo o piso**: abaixo disso empilha, senao no tablet os
            campos de dinheiro espremeriam.
            ⚠️ **`items-start`**: sem ele o grid estica as tres a mesma altura e as mais curtas
            ficam com um vazio pendurado no fim. */}
        <div className={mostrarIdentificacao ? 'grid lg:grid-cols-[minmax(0,4fr)_minmax(0,2fr)_minmax(0,4fr)] gap-x-6 items-start' : undefined}>
        {/* A trava de identificacao ([DA-697]) so' aparece quando falta: nas 341 vendas de 30 dias
            que ja tinham cliente com telefone, este modal segue como era — uma coluna. */}
        {mostrarIdentificacao && (
          <div className="min-w-0">
          <BlocoIdentificacaoCliente
            orcamentoId={orcamentoId}
            clienteId={clienteId}
            clienteNome={clienteNome}
            clienteSemNumero={clienteId > 1 && clienteTemNumero === false}
            whatsappOpcional={whatsappOpcional}
            onAbrirBuscaCliente={() => setShowBuscaCliente(true)}
            onCapturou={(contatoId, nome) => {
              setCapturadoAgora(contatoId);
              setCapturadoNome(nome || null);
              onCapturouContato?.(contatoId, nome);
            }}
            // A ficha que a captura achou/criou entra pelo MESMO gesto do "Escolher cliente"
            // ([DA-698]): quem grava o `cliente_id` no pedido segue sendo o autosave da tela, e o
            // envio ja espera por ele. Duas maos gravando o mesmo campo seria o desenho errado.
            onClienteResolvido={(c) => onEscolherCliente?.(c)}
            // O recibo de captura deste modal era do cliente que saiu: some junto.
            onRemoverCliente={onRemoverCliente ? () => {
              setCapturadoAgora(null);
              setCapturadoNome(null);
              onRemoverCliente();
            } : undefined}
            telefoneRecusado={telefoneRecusado}
            recusaNomeInicial={recusaNome}
            onRecusou={(nome, ids) => {
              setRecusadoAgora(nome);
              onRecusarTelefone?.(nome, ids);
            }}
            etiquetas={etiquetasDoCombo}
            etq={etq}
            onEtqMudou={setEtq}
            onExigenciaMudou={setEtqPendente}
          />
          {/* De onde o cliente conheceu a loja — ABAIXO do "Quem esta comprando?" (pedido dele,
              16/10/2026, migration 0322). Opcional: nao entra na trava. */}
          <BlocoOrigemCliente
            orcamentoId={orcamentoId} clienteId={clienteId} clienteNome={clienteNome}
            marcouAqui={origemMarcadaAqui} onMarcou={() => setOrigemMarcadaAqui(true)}
          />
          </div>
        )}

        {/* ── A coluna do MEIO: a area de atuacao de quem esta comprando ([DA-723]) ──────────── */}
        {mostrarIdentificacao && (
          <div className="min-w-0">
            <ListboxEtiquetasVenda
              etiquetas={etiquetasDoCombo}
              estado={etq}
              onAlternar={(id) => setEtq((a) => alternarEtiqueta(a, id, usuario?.perfil === 'admin'))}
              ehAdmin={usuario?.perfil === 'admin'}
              pendente={etqPendente}
            />
          </div>
        )}

        {/* ── A terceira coluna: o que a venda vai ser (nota, pagamento, totais) ─────────────── */}
        <div className="min-w-0">
        {/* ⛔ **A etiqueta com o cliente JA identificado** ([DA-722], pedido dele: *"quando eu
            escolho um cliente, ele nao permite selecionar a Etiqueta"*). Aparece justamente quando
            o bloco de identificacao SUMIU — ali a trava ja foi satisfeita e, ate hoje, a area de
            atuacao ficava sem porta. ⚠️ Grava na hora (o contato ja existe, nao ha o que capturar)
            e **nao exige nada**: esticar a obrigatoriedade pra ca travaria as 45 vendas do caminho
            1, e trava que barra venda e' decisao dele. */}
        {!mostrarIdentificacao && (
          <CampoEtiquetasVenda clienteId={clienteId} contatoId={capturadoAgora} />
        )}
        {/* Com o cliente ja identificado o bloco de cima sumiu, mas a origem continua visivel —
            "quando o usuario identificar o cliente, ja aparece de onde ele veio" (pedido dele). */}
        {!mostrarIdentificacao && (
          <BlocoOrigemCliente
            orcamentoId={orcamentoId} clienteId={clienteId} clienteNome={clienteNome}
            marcouAqui={origemMarcadaAqui} onMarcou={() => setOrigemMarcadaAqui(true)}
          />
        )}
        {/* O recibo do gesto: uma linha, no lugar onde o bloco estava. */}
        {!mostrarIdentificacao && capturadoAgora && (
          <div className="mb-3 rounded-lg border border-green-300 bg-green-50 px-2.5 py-1.5 text-[12px] text-green-800 flex items-center gap-1.5">
            <Check size={13} className="flex-shrink-0" />
            <span>
              Anotado{capturadoNome ? <>: <b>{capturadoNome}</b></> : ''}
              {clienteId > 1 ? <> · a venda vai no nome de <b>{clienteNome}</b></> : ' · entrou na lista do WhatsApp'}
            </span>
          </div>
        )}
        {!mostrarIdentificacao && !capturadoAgora && recusadoAgora && (
          <div className="mb-3 rounded-lg border border-gray-300 bg-gray-50 px-2.5 py-1.5 text-[12px] text-gray-700 flex items-center gap-1.5">
            <Check size={13} className="flex-shrink-0" />
            <span>
              Registrado: <b>{recusadoAgora}</b> nao quis informar o telefone · entra no placar
            </span>
          </div>
        )}
        {/* Tipo de nota (toggle compacto) + CPF na MESMA linha — economiza altura vertical */}
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Nota:</span>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {!travadoA4 && (
              <button
                onClick={() => setTipoDocumento('nfce')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
                  tipoDocumento === 'nfce' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Receipt size={15} /> NFC-e Consumidor
              </button>
              )}
              <button
                onClick={() => setTipoDocumento('nfe')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-l border-gray-300 transition ${
                  tipoDocumento === 'nfe' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FileText size={15} /> NF-e A4
              </button>
            </div>
            {/* Botao que some tem que dizer por que — senao a vendedora procura o NFC-e que sumiu. */}
            {travadoA4 && (
              <span className="text-xs text-gray-500">
                {clienteNome} so recebe <b>Nota A4</b> (travado no cadastro)
              </span>
            )}
          </div>
          {tipoDocumento === 'nfce' && isConsumidor && (
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                CPF <span className="text-gray-400 font-normal">(opc.)</span>
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={cpfNota}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                  const formatted = digits
                    .replace(/(\d{3})(\d)/, '$1.$2')
                    .replace(/(\d{3})(\d)/, '$1.$2')
                    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                  setCpfNota(formatted);
                }}
                placeholder="000.000.000-00"
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          )}
        </div>

        {nfeComConsumidor && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-red-700">NF-e requer cliente cadastrado</p>
              <p className="text-red-600 mt-1">
                O cliente "{clienteNome}" nao possui dados completos para emissao de NF-e. Selecione NFC-e ou
                altere o cliente no orcamento.
              </p>
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-600 mb-1.5">Forma de Pagamento</label>
          <div className="space-y-2">
            {pagamentos.map((pag, idx) => {
              const fp = getFp(pag.forma_pagamento_id);
              const semDesc = fp && !fp.permite_desconto;
              const crediario = fp && fp.tipo === 'crediario';
              const credito = fp && fp.tipo === 'credito_loja';
              const cartaoCredito = fp && fp.tipo === 'cartao_credito';
              return (
                <div
                  key={idx}
                  className={`rounded-lg border p-2 ${
                    credito
                      ? 'border-green-200 bg-green-50'
                      : semDesc && temDescontos
                      ? 'border-orange-200 bg-orange-50'
                      : crediario
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={pag.forma_pagamento_id}
                      onChange={(e) => atualizarPagamento(idx, 'forma_pagamento_id', Number(e.target.value))}
                      onKeyDown={handleKeyDown}
                      className="flex-1 min-w-[140px] text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      {formasDisponiveis(pag.forma_pagamento_id).map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nome}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">R$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          editingValores[idx] !== undefined
                            ? editingValores[idx]
                            : pag.valor === ''
                            ? ''
                            : fmtMoney(pag.valor)
                        }
                        onFocus={() =>
                          setEditingValores((m) => ({
                            ...m,
                            [idx]: pag.valor === '' ? '' : fmtMoney(pag.valor),
                          }))
                        }
                        onChange={(e) => {
                          const s = e.target.value;
                          setEditingValores((m) => ({ ...m, [idx]: s }));
                          atualizarPagamento(idx, 'valor', String(parseMoney(s)));
                        }}
                        onBlur={() => {
                          setEditingValores((m) => {
                            const { [idx]: _ignored, ...rest } = m;
                            return rest;
                          });
                          handleValorBlur(idx);
                        }}
                        onKeyDown={handleKeyDown}
                        data-pag-valor
                        className="w-24 text-right text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                    </div>
                    {cartaoCredito && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-gray-500 uppercase">Conta</span>
                        <select
                          value={contaEfetiva(idx)}
                          onChange={(e) => setContaSel((m) => ({ ...m, [idx]: e.target.value }))}
                          onKeyDown={handleKeyDown}
                          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
                        >
                          <option value="">Selecione…</option>
                          {contasCartao.map((c) => (
                            <option key={c.id} value={c.id}>{c.nome}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {pagamentos.length > 1 && (
                      <button
                        onClick={() => removerPagamento(idx)}
                        className="text-red-400 hover:text-red-600 transition p-1"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  {crediario && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <CalendarClock size={14} className="text-purple-600" />
                      <span className="text-purple-700">Dias para vencimento:</span>
                      <input
                        type="number"
                        value={pag.dias_vencimento || 15}
                        onChange={(e) => atualizarPagamento(idx, 'dias_vencimento', e.target.value)}
                        onKeyDown={handleKeyDown}
                        min="1"
                        max="365"
                        className="w-16 text-right text-sm border border-purple-300 rounded-lg px-2 py-1 focus:ring-1 focus:ring-purple-500 outline-none"
                      />
                      <span className="text-xs text-purple-600">dias</span>
                    </div>
                  )}
                  {fpIsDinheiro(pag.forma_pagamento_id) && (() => {
                    const valorLinha = parseFloat(String(pag.valor)) || 0;
                    const recebido = pag.recebido ?? valorLinha;
                    const troco = Math.max(0, +(recebido - valorLinha).toFixed(2));
                    const temTroco = troco > 0.005;
                    return (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Recebido</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={editingRecebidos[idx] !== undefined ? editingRecebidos[idx] : fmtMoney(recebido)}
                            onFocus={() => setEditingRecebidos((m) => ({ ...m, [idx]: fmtMoney(recebido) }))}
                            onChange={(e) => {
                              const s = e.target.value;
                              setEditingRecebidos((m) => ({ ...m, [idx]: s }));
                              const v = parseMoney(s);
                              setPagamentos((prev) => prev.map((p, i) => i === idx ? { ...p, recebido: v } : p));
                            }}
                            onBlur={() => setEditingRecebidos((m) => { const { [idx]: _, ...rest } = m; return rest; })}
                            onKeyDown={handleKeyDown}
                            className="w-full text-right text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none font-semibold"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: temTroco ? '#7c3aed' : '#9ca3af' }}>Troco</label>
                          <div
                            className="w-full text-right text-sm rounded-lg px-2 py-1.5 font-bold"
                            style={{
                              background: temTroco ? '#ede9fe' : '#f1f5f9',
                              border: `1px solid ${temTroco ? '#ddd6fe' : '#e5e7eb'}`,
                              color: temTroco ? '#7c3aed' : '#9ca3af',
                            }}
                          >
                            {fmtMoney(troco)}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {credito && (
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <Gift size={14} className="text-green-600" />
                      {creditoLoading ? (
                        <span className="text-green-600">Buscando saldo...</span>
                      ) : saldoCredito !== null ? (
                        <span className="text-green-700">
                          Saldo disponivel: <strong>R$ {fmt(saldoCredito)}</strong>
                          {/* Diz de QUAL carteira, senao o numero fica sem explicacao pra quem
                              sabe que o cliente tem credito dos dois tipos (DA-436). */}
                          {carteiraCredito === 'programa' && ' (Parceiro FIODECOBRE)'}
                        </span>
                      ) : null}
                    </div>
                  )}
                  {cartaoCredito && (() => {
                    const est = parcelamentos[idx];
                    const conta = contaEfetiva(idx); // conta/maquininha (escolhida ou default)
                    const planos = est?.data?.parcelas ?? [];
                    const nSel = parcelasSel[idx] ?? 1;
                    const sel = planos.find((x) => x.n === nSel);
                    const semJurosAte = est?.data?.sem_juros_ate ?? 1;
                    // As parcelas so liberam depois de escolher a conta/maquininha.
                    const parcelasTravadas = !conta || !est?.data;
                    return (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-white p-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-gray-500 uppercase">Parcelas</span>
                          <select
                            value={nSel}
                            disabled={parcelasTravadas}
                            onChange={(e) => setParcelasSel((m) => ({ ...m, [idx]: Number(e.target.value) }))}
                            onKeyDown={handleKeyDown}
                            className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {planos.length === 0 ? (
                              <option value={1}>1x</option>
                            ) : (
                              planos.map((x) => (
                                <option key={x.n} value={x.n}>
                                  {x.n}x de R$ {fmtMoney(x.valor_parcela / 100)}{x.sem_juros ? ' sem juros' : x.juros_mes ? ` · ${x.juros_mes.toFixed(1).replace('.', ',')}% a.m.` : ''}
                                </option>
                              ))
                            )}
                          </select>
                          {est?.loading && (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Loader2 size={12} className="animate-spin" /> consultando taxas…
                            </span>
                          )}
                        </div>
                        {est?.erro && !est.loading && (
                          <p className="text-xs font-semibold mt-1" style={{ color: '#92400e' }}>
                            Parcelamento indisponivel — cobre 1x no preco de tabela.
                          </p>
                        )}
                        {!conta && !est?.loading && !est?.erro && (
                          <p className="text-xs mt-1 text-gray-500">Escolha a conta/maquininha pra ver as parcelas e o juros.</p>
                        )}
                        {sel && !est?.loading && !est?.erro && (
                          <div className="mt-1.5 flex items-center justify-between flex-wrap gap-1">
                            <div>
                              <span className="text-xs text-gray-500 uppercase">Cobrar na maquininha </span>
                              <span className="font-bold tabular-nums" style={{ fontSize: 15 }}>R$ {fmtMoney(sel.total / 100)}</span>
                              <span className="text-xs text-gray-500"> · {sel.n}x de R$ {fmtMoney(sel.valor_parcela / 100)}</span>
                              {!!sel.juros_mes && sel.juros_mes > 0 && (
                                <span className="text-xs font-semibold text-gray-700"> · {sel.juros_mes.toFixed(1).replace('.', ',')}% a.m.</span>
                              )}
                            </div>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#fde68a', color: '#000' }}>
                              {semJurosAte > 1 ? `Sem juros ate ${semJurosAte}x` : 'Juros a partir do 2x'}
                              {sel.n > semJurosAte ? ' · esta tem juros' : ''}
                            </span>
                          </div>
                        )}
                        {est?.data && !est.loading && !est.erro && (
                          <div className="mt-2">
                            <SimulacaoParcelamentoWhatsApp
                              parcelas={est.data.parcelas}
                              provedor={conta === 'pagbank' ? 'PagBank' : 'Mercado Pago'}
                              orcamentoId={orcamentoId}
                              numero={numeroOrcamento}
                              clienteId={clienteId}
                              clienteNome={clienteNome}
                              operador={usuario?.nome}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          {/* Sempre VISIVEL, cinza quando nao cabe (DA-468: esconder e impedir nao sao a mesma
              coisa — sumido, o banner "Falta R$ X" mandava clicar num botao inexistente). Acende
              exatamente enquanto falta valor, igual a tela de finalizar venda. (DA-582) */}
          <button
            onClick={adicionarPagamento}
            disabled={!faltaCobertura || pagamentos.length >= formasPagamento.length}
            title={
              pagamentos.length >= formasPagamento.length
                ? 'Todas as formas de pagamento já estão em uso neste orçamento.'
                : faltaCobertura
                  ? `Falta R$ ${fmt(restante)} — lance aqui a outra forma que o cliente vai pagar.`
                  : 'Recebimento já coberto. Pra dividir, baixe o valor de uma linha — o que faltar acende este botão.'
            }
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2 transition disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:text-gray-400"
          >
            <Plus size={14} />
            Adicionar forma de pagamento
          </button>
        </div>

        {(() => {
          const valorCredito = pagamentos
            .filter((p) => fpIsCredito(p.forma_pagamento_id))
            .reduce((s, p) => s + (parseFloat(String(p.valor)) || 0), 0);
          const totalLiquido = Math.max(0, Math.round((totalEfetivo - valorCredito) * 100) / 100);
          // DA-140: juros+imposto do parcelamento no cartao (mesma conta do caixa: total - tabela).
          // Entra no "Total a Pagar" pra a operadora ver o valor REAL cobrado do cliente / da nota
          // ANTES de enviar. Nao vira valor de pagamento aqui (o caixa recalcula ao vivo); so exibe.
          const acrescimoCartao = Math.round(pagamentos.reduce((acc, p, i) => {
            if (getFp(p.forma_pagamento_id)?.tipo !== 'cartao_credito') return acc;
            const est = parcelamentos[i];
            const sel = est?.data?.parcelas.find((x) => x.n === (parcelasSel[i] ?? 1));
            if (!sel || !est?.data) return acc;
            const a = (sel.total - est.data.tabela) / 100;
            return a > 0 ? acc + a : acc;
          }, 0) * 100) / 100;
          const totalACobrar = Math.round((totalLiquido + acrescimoCartao) * 100) / 100;
          return (
            <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-2.5 space-y-1">
              <div className="flex justify-between text-sm text-gray-700">
                <span>Subtotal do Orcamento</span>
                <span className="tabular-nums">R$ {fmt(totalOrcamento)}</span>
              </div>
              {descontoRemovido > 0 && (
                <div className="flex justify-between text-xs text-orange-600">
                  <span>Acrescimo por pagamento em cartao</span>
                  <span className="tabular-nums">+R$ {fmt(descontoRemovido)}</span>
                </div>
              )}
              {valorCredito > 0 && (
                <div className="flex justify-between text-sm text-green-700">
                  <span className="flex items-center gap-1">
                    <Gift size={12} /> Credito da Loja
                  </span>
                  <span className="tabular-nums">−R$ {fmt(valorCredito)}</span>
                </div>
              )}
              {acrescimoCartao > 0.005 && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Juros do parcelamento no cartao</span>
                  <span className="tabular-nums">+R$ {fmt(acrescimoCartao)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-1.5 border-t border-blue-200">
                <span className="text-sm font-semibold text-blue-900">Total a Pagar</span>
                <span className="text-2xl font-bold text-blue-700 tabular-nums">R$ {fmt(totalACobrar)}</span>
              </div>
              {valorCredito > 0 && totalLiquido > 0 && (
                <div className="text-xs text-blue-600 text-right">
                  R$ {fmt(valorCredito)} usados do credito + R$ {fmt(totalACobrar)} a pagar agora
                </div>
              )}
              {valorCredito > 0 && totalLiquido === 0 && (
                <div className="text-xs text-green-700 text-right font-medium">
                  Pago integralmente com credito da loja
                </div>
              )}
            </div>
          );
        })()}

        {faltaCobertura && (
          <div className="mb-3 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={18} className="text-orange-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-orange-700">
              <p className="font-medium">Falta R$ {fmt(restante)} sem forma de pagamento</p>
              <p className="mt-1">
                O total do orcamento nao esta coberto. Clique em <strong>"Adicionar forma de pagamento"</strong> e
                informe como o cliente pagou os R$ {fmt(restante)} restantes antes de enviar ao caixa.
              </p>
            </div>
          </div>
        )}

        {temSemDescSelecionado && temDescontos && descontoRemovido > 0 && (
          <div className="mb-3 bg-orange-50 border border-orange-200 rounded-lg py-2 px-3 flex items-center gap-2 text-sm text-orange-700">
            <AlertTriangle size={16} className="text-orange-500 flex-shrink-0" />
            <span>
              <span className="font-medium">Desconto ajustado (cartao):</span> R$ {fmt(totalOrcamento)} → R$ {fmt(totalEfetivo)}
            </span>
          </div>
        )}

        {crediarioComConsumidor && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Crediario exige cliente cadastrado</p>
              <p className="mt-1">Selecione um cliente cadastrado no orcamento para utilizar crediario.</p>
            </div>
          </div>
        )}

        {crediarioDescontoAcima5 && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Desconto excedido para crediario</p>
              <p className="mt-1">
                Crediario permite no maximo 5% de desconto. Atual: {descontoPercentual.toFixed(1)}%.
              </p>
            </div>
          </div>
        )}

        {creditoComConsumidor && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Credito da Loja exige cliente cadastrado</p>
              <p className="mt-1">Selecione um cliente cadastrado no orcamento para utilizar credito.</p>
            </div>
          </div>
        )}

        {creditoSemSaldo && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <p className="font-medium">Cliente sem credito disponivel</p>
              <p className="mt-1">O cliente nao possui credito de troca disponivel.</p>
            </div>
          </div>
        )}
        </div>{/* fim da coluna da direita */}
        </div>{/* fim do grid de duas colunas */}

        </div>

        {/* ⚠️ **O rodape acompanha a TERCEIRA coluna** ([DA-723], pedido dele com print: *"diminua
            o tamanho destes dois botoes, para caber so' na terceira coluna"*). Com o modal em
            1400px os dois esticavam a largura inteira e ficavam com 660px cada, longe da coluna
            que eles concluem.
            ⛔ **A largura sai do MESMO template do grid do miolo, nao de um percentual calculado a
            mao**: assim a proporcao muda num lugar so' e o rodape acompanha sozinho — dois numeros
            pra manter em sincronia e' o buraco da [DA-659].
            ⚠️ Abaixo de `lg:` o grid vira uma coluna e os botoes voltam a largura cheia, que e' o
            que o tablet precisa; e sem o bloco de identificacao o modal e' de uma coluna so' e o
            rodape segue como sempre foi. */}
        <div className={`px-6 py-4 flex-shrink-0 border-t border-gray-100${mostrarIdentificacao ? ' grid lg:grid-cols-[minmax(0,4fr)_minmax(0,2fr)_minmax(0,4fr)] gap-x-6' : ''}`}>
        <div className={`flex gap-2${mostrarIdentificacao ? ' lg:col-start-3' : ''}`}>
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
          >
            Cancelar
          </button>
          {temCrediario && (
            <BotaoAutorizacao
              variante="modal"
              numero={numeroOrcamento}
              clienteNome={clienteNome}
              clienteDoc={clienteDoc}
              comprador={comprador}
              itens={itensOrcamento}
              total={totalOrcamento}
              formaPagamento={pagamentos.map((p) => getFp(p.forma_pagamento_id)?.nome || '').filter(Boolean).join(' + ')}
            />
          )}
          <button
            onClick={handleConfirm}
            disabled={enviando || faltaIdentificar || nfeComConsumidor || crediarioComConsumidor || crediarioDescontoAcima5 || creditoComConsumidor || creditoSemSaldo || faltaCobertura}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enviando ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send size={16} />
                Enviar
              </>
            )}
          </button>
        </div>
        </div>
      </div>

      {/* Seletor de cliente ABRINDO DE DENTRO deste modal: `z-[1100]`, o andar de quem nasce
          sobre outro modal — o proprio `ModalBuscarCliente` ja carrega essa camada. Peca reusada,
          nao uma segunda busca de cliente. */}
      {showBuscaCliente && (
        <ModalBuscarCliente
          onSelect={(c) => {
            onEscolherCliente?.(c);
            setShowBuscaCliente(false);
          }}
          onClose={() => setShowBuscaCliente(false)}
        />
      )}
    </div>
  );
}
